"use client";

import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  CalendarClock,
  ChevronRight,
  Clock,
  Eye,
  MapPin,
  Plus,
  Ticket,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import {
  Avatar,
  Badge,
  ConfirmDialog,
  FilterSearch,
  FilterSelect,
  FiltersToolbar,
  PageHeader,
  Tooltip,
  cn,
  useToast,
} from "@chotca/ui";
import type { EmployerApplicant, EmployerApplicantTab } from "@chotca/config";
import {
  getSdk,
  qk,
  useEmployerApplicants,
  useEmployerApplicantCounts,
  useMe,
  usePlanStatus,
  useOrgAddresses,
} from "@chotca/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useTableFilters } from "@/lib/use-table-filters";
import {
  DataTable,
  usePagination,
  type BulkAction,
  type Column,
  type RowAction,
} from "@/components/data-table";
import { WorkerProfileModal } from "@/components/worker-profile-modal";
import { InviteApplicantDialog } from "@/components/employer/invite-applicant-dialog";
import { useSubscriptionModals } from "@/components/employer/subscription-modals";

const TABS: EmployerApplicantTab[] = [
  "new",
  "invited",
  "scheduled",
  "rejected",
];

const WD = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
function fmtSlot(isoStr: string): string {
  const d = new Date(isoStr);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${WD[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1} · ${hh}:${mm}`;
}
function fmtAgo(isoStr: string): string {
  const diff = Date.now() - new Date(isoStr).getTime();
  const h = Math.floor(diff / 3_600_000);
  if (h < 1) return "vừa xong";
  if (h < 24) return `${h} giờ trước`;
  return `${Math.floor(h / 24)} ngày trước`;
}

export function ApplicantsClient() {
  const t = useTranslations("applicants");
  const qc = useQueryClient();
  const { toast } = useToast();
  const { openUpgrade, openTopup } = useSubscriptionModals();
  const me = useMe();
  const orgId = me.data?.memberships[0]?.orgId;
  const addresses = useOrgAddresses(orgId ?? null);
  const plan = usePlanStatus(orgId);
  const credits = plan.data?.sourcingRemaining ?? 0;
  const isFree = (plan.data?.effectiveCode ?? "FREE") === "FREE";

  const [tab, setTab] = React.useState<EmployerApplicantTab>("new");
  const filters = useTableFilters({
    search: { debounce: 300 },
    orgAddressId: {},
  });
  const { page, pageSize, setPage, setPageSize } = usePagination();

  const addressId = filters.applied.orgAddressId || undefined;
  const { data, isLoading, isFetching } = useEmployerApplicants({
    tab,
    orgAddressId: addressId,
    search: filters.applied.search || undefined,
    sort: "latest",
    page,
    limit: pageSize,
  });
  const counts = useEmployerApplicantCounts(addressId);

  // Reset to page 1 whenever the tab or any filter changes.
  const resetKey = `${tab}|${JSON.stringify(filters.applied)}`;
  React.useEffect(() => setPage(1), [resetKey, setPage]);

  const [profileApp, setProfileApp] = React.useState<EmployerApplicant | null>(
    null,
  );
  const [inviteApp, setInviteApp] = React.useState<EmployerApplicant | null>(
    null,
  );
  const [confirmReject, setConfirmReject] =
    React.useState<EmployerApplicant | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link from the activity log: /applicants?app=<id> opens the candidate
  // profile read-only. The modal self-fetches by id, so the application need
  // not be on the current page.
  const deepLinkAppId = searchParams.get("app");
  function clearDeepLink() {
    const next = new URLSearchParams(searchParams);
    next.delete("app");
    setSearchParams(next, { replace: true });
  }

  function invalidate() {
    qc.invalidateQueries({ queryKey: qk.applications.employer() });
    qc.invalidateQueries({ queryKey: qk.applications.employerCounts() });
    qc.invalidateQueries({ queryKey: qk.applications.pending });
  }

  const reject = useMutation({
    mutationFn: (id: string) => getSdk().applications.reject(id),
    onSuccess: () => {
      invalidate();
      toast({ tone: "success", title: t("rejectDone") });
      setConfirmReject(null);
      setProfileApp(null);
    },
    onError: () => toast({ tone: "error", title: t("rejectError") }),
  });

  const bulkReject = useMutation({
    mutationFn: (ids: string[]) => getSdk().applications.rejectBatch(ids),
    onSuccess: (res) => {
      invalidate();
      toast({
        tone: "success",
        title: t("bulkDone", { n: res.rejectedCount }),
      });
    },
    onError: () => toast({ tone: "error", title: t("rejectError") }),
  });

  const list = data?.items ?? [];

  const candidateCol: Column<EmployerApplicant> = {
    key: "cand",
    header: t("colApplicant"),
    cell: (a) => (
      <div className="flex items-center gap-3">
        <Avatar
          name={a.worker.fullName}
          src={a.worker.avatarUrl ?? undefined}
          size="md"
          tone="lime"
        />
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 font-semibold text-ink">
            {a.worker.fullName}
            {a.worker.kycVerifiedAt ? (
              <Badge tone="lime-soft">✓ CCCD</Badge>
            ) : null}
          </p>
          <p className="truncate text-xs text-ash">{a.postingTitle}</p>
        </div>
      </div>
    ),
  };
  const areaCol: Column<EmployerApplicant> = {
    key: "area",
    header: t("colArea"),
    cell: (a) => (
      <span className="text-sm text-ink/80">
        {a.worker.currentDistrict ?? "—"}
      </span>
    ),
  };
  const expCol: Column<EmployerApplicant> = {
    key: "exp",
    header: t("colExperience"),
    cell: (a) => (
      <span className="text-sm text-ash">
        {a.experienceSummary ?? t("noExperience")}
      </span>
    ),
  };
  const appliedCol: Column<EmployerApplicant> = {
    key: "applied",
    header: t("colApplied"),
    cell: (a) => (
      <span className="text-xs text-ash">
        {fmtAgo(a.application.appliedAt)}
      </span>
    ),
  };
  const scheduleCol: Column<EmployerApplicant> = {
    key: "schedule",
    header: t("colSchedule"),
    cell: (a) =>
      a.scheduledSlot ? (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-ink">
            {fmtSlot(a.scheduledSlot.startAt)}
          </span>
          <Badge tone={a.scheduledSlot.committed ? "lime-soft" : "sun-soft"}>
            {a.scheduledSlot.committed ? t("confirmed") : t("editable")}
          </Badge>
        </div>
      ) : (
        <span className="text-xs text-ash">—</span>
      ),
  };

  const columns: Column<EmployerApplicant>[] =
    tab === "scheduled"
      ? [candidateCol, scheduleCol, areaCol, expCol]
      : [candidateCol, areaCol, expCol, appliedCol];

  const rowActions: RowAction<EmployerApplicant>[] = [
    {
      key: "view",
      label: t("actView"),
      icon: Eye,
      onClick: (a) => setProfileApp(a),
    },
    {
      key: "invite",
      label: t("actInvite"),
      icon: UserPlus,
      onClick: (a) => setInviteApp(a),
      show: () => tab === "new",
    },
    {
      key: "reject",
      label: t("actReject"),
      icon: X,
      destructive: true,
      onClick: (a) => setConfirmReject(a),
      show: () => tab === "new",
    },
  ];

  const bulkActions: BulkAction<EmployerApplicant>[] =
    tab === "new"
      ? [
          {
            key: "reject",
            label: t("bulkReject"),
            icon: X,
            destructive: true,
            onClick: (rows) =>
              bulkReject.mutate(rows.map((r) => r.application.id)),
            loading: () => bulkReject.isPending,
          },
        ]
      : [];

  const addressOptions = (addresses.data?.items ?? []).map((a) => ({
    value: a.id,
    label: a.alias,
  }));

  const tabCount = (tk: EmployerApplicantTab): number | undefined => {
    if (!counts.data) return undefined;
    return counts.data[tk];
  };

  // Touch-friendly card for the <md viewport. Tapping the body opens the
  // profile (same as a table row click); per-card buttons replace the table's
  // hover-only row actions, which a phone can never reveal.
  const renderMobileCard = (a: EmployerApplicant) => (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setProfileApp(a)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setProfileApp(a);
        }
      }}
      className="block w-full rounded-2xl border-[1.5px] border-smoke bg-paper p-3.5 text-left transition active:scale-[0.99] active:bg-mist/40"
    >
      <div className="flex items-start gap-3">
        <Avatar
          name={a.worker.fullName}
          src={a.worker.avatarUrl ?? undefined}
          size="md"
          tone="lime"
        />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 font-semibold text-ink">
            <span className="truncate">{a.worker.fullName}</span>
            {a.worker.kycVerifiedAt ? (
              <Badge tone="lime-soft">✓ CCCD</Badge>
            ) : null}
          </p>
          <p className="truncate text-xs text-ash">{a.postingTitle}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ash">
            {a.worker.currentDistrict ? (
              <span className="inline-flex items-center gap-1">
                <MapPin size={12} />
                {a.worker.currentDistrict}
              </span>
            ) : null}
            {tab === "scheduled" && a.scheduledSlot ? (
              <span className="inline-flex items-center gap-1 font-semibold text-ink">
                <CalendarClock size={12} />
                {fmtSlot(a.scheduledSlot.startAt)}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1">
                <Clock size={12} />
                {fmtAgo(a.application.appliedAt)}
              </span>
            )}
          </div>
          {tab === "scheduled" && a.scheduledSlot ? (
            <Badge
              tone={a.scheduledSlot.committed ? "lime-soft" : "sun-soft"}
              className="mt-2"
            >
              {a.scheduledSlot.committed ? t("confirmed") : t("editable")}
            </Badge>
          ) : null}
        </div>
        <ChevronRight size={18} className="mt-0.5 shrink-0 text-ash" />
      </div>

      {tab === "new" ? (
        <div className="mt-3 flex gap-2 border-t border-smoke pt-3">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setInviteApp(a);
            }}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-lime px-3 py-2 text-sm font-bold text-ink transition active:brightness-95"
          >
            <UserPlus size={15} strokeWidth={2} />
            {t("actInvite")}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmReject(a);
            }}
            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-smoke px-4 py-2 text-sm font-semibold text-rose-700 transition active:bg-rose-50"
          >
            <X size={15} strokeWidth={2} />
            {t("actReject")}
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-4">
      <PageHeader title={t("title")} description={t("subtitle")} />

      {/* Tab bar */}
      <div className="flex gap-1 overflow-x-auto border-b border-smoke">
        {TABS.map((tk) => {
          const active = tk === tab;
          const c = tabCount(tk);
          return (
            <button
              key={tk}
              type="button"
              onClick={() => setTab(tk)}
              className={cn(
                "relative whitespace-nowrap px-3 py-2.5 text-sm font-semibold transition-colors",
                active ? "text-ink" : "text-ash hover:text-ink",
              )}
            >
              {t(`tab_${tk}`)}
              {c !== undefined ? (
                <span
                  className={cn(
                    "ml-1.5 rounded-full px-1.5 py-0.5 text-[11px] font-bold tabular-nums",
                    active ? "bg-lime text-ink" : "bg-mist text-ash",
                  )}
                >
                  {c}
                </span>
              ) : null}
              {active ? (
                <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-lime" />
              ) : null}
            </button>
          );
        })}
      </div>

      <FiltersToolbar
        hasActiveFilters={filters.hasAny}
        onClearAll={filters.clear}
        actions={
          tab === "new" ? (
            isFree ? (
              // FREE can't source — CTA opens the upgrade modal instead of
              // navigating. Animated to draw attention.
              <button
                type="button"
                onClick={() => openUpgrade()}
                className="inline-flex animate-pulse items-center gap-1.5 rounded-full bg-lime px-4 py-2 text-sm font-bold text-ink shadow-[0_0_0_0_rgba(0,0,0,0)] transition hover:brightness-95"
              >
                <Users size={16} strokeWidth={2.25} />
                {t("findCandidates")}
              </button>
            ) : (
              <Link
                to="/candidates"
                className="inline-flex animate-pulse items-center gap-1.5 rounded-full bg-lime px-4 py-2 text-sm font-bold text-ink transition hover:brightness-95"
              >
                <Users size={16} strokeWidth={2.25} />
                {t("findCandidates")}
              </Link>
            )
          ) : null
        }
      >
        <FilterSearch
          value={filters.values.search ?? ""}
          onChange={(v) => filters.set("search", v)}
          placeholder={t("searchPlaceholder")}
          width="min-w-0 flex-1 sm:w-72 sm:flex-none"
        />
        <FilterSelect
          value={filters.values.orgAddressId ?? ""}
          onValueChange={(v) => filters.set("orgAddressId", v)}
          placeholder={t("allBranches")}
          options={addressOptions}
          width="w-36 shrink-0 sm:w-44"
        />
        {/* Clickable lượt-mời badge. FREE → upgrade (no sourcing yet); Pro+ →
            buy a top-up pack. Tooltip explains what the number means. */}
        <Tooltip
          label={
            isFree
              ? "Lượt mời phỏng vấn — nâng cấp gói để mở khoá săn ứng viên"
              : "Lượt mời phỏng vấn còn — bấm để mua thêm"
          }
        >
          <button
            type="button"
            onClick={() => (isFree ? openUpgrade() : openTopup("SOURCING"))}
            className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full bg-lime px-3.5 text-sm font-bold text-ink transition hover:brightness-95"
          >
            <Ticket size={16} strokeWidth={2} />
            {credits}
            <Plus size={14} strokeWidth={2.5} className="ml-0.5 opacity-70" />
          </button>
        </Tooltip>
      </FiltersToolbar>

      <DataTable
        columns={columns}
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(a) => a.application.id}
        rowActions={rowActions}
        bulkActions={bulkActions}
        onRowClick={(a) => setProfileApp(a)}
        page={page}
        pageSize={pageSize}
        total={data?.total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        emptyText={t("empty")}
        emptyDescription={t("emptyDesc")}
        renderMobileCard={renderMobileCard}
      />

      {profileApp ? (
        <WorkerProfileModal
          applicationId={profileApp.application.id}
          open
          onClose={() => setProfileApp(null)}
          onInvite={() => {
            const a = profileApp;
            setProfileApp(null);
            setInviteApp(a);
          }}
          onReject={() => setConfirmReject(profileApp)}
          invitePending={false}
          rejectPending={reject.isPending}
          inviteLabel={t("actInvite")}
        />
      ) : null}

      {deepLinkAppId ? (
        <WorkerProfileModal
          applicationId={deepLinkAppId}
          open
          readOnly
          onClose={clearDeepLink}
        />
      ) : null}

      {inviteApp && orgId ? (
        <InviteApplicantDialog
          open
          onClose={() => setInviteApp(null)}
          onInvited={() => {
            invalidate();
            setInviteApp(null);
          }}
          orgId={orgId}
          applicationId={inviteApp.application.id}
          workerName={inviteApp.worker.fullName}
        />
      ) : null}

      <ConfirmDialog
        open={!!confirmReject}
        onClose={() => setConfirmReject(null)}
        onConfirm={() => {
          if (confirmReject) reject.mutate(confirmReject.application.id);
        }}
        title={t("rejectTitle")}
        description={t("rejectDesc")}
        confirmLabel={t("actReject")}
        destructive
        loading={reject.isPending}
      />
    </div>
  );
}
